// src/component/MusicGeoPrefectureStats.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Chip, Stack } from "@mui/material";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";

import { db } from "../api/firebase";
import { doc, getDoc } from "firebase/firestore";
import { AppThemeContext } from "./AppShell";
import MusicPrefectureMap from "./PreviewMusicPrefectureMap";
import { useDayBoundary } from "../context/DayBoundaryContext";

// ★ メインスレッドに制御を返す（描画詰まり対策）
const yieldToMain = () => new Promise((r) => setTimeout(r, 0));

// ★ アイドル時に処理（再生開始直後の体感を守る）
const runWhenIdle = (fn, timeout = 1200) => {
  if (typeof window === "undefined") return fn();
  if ("requestIdleCallback" in window) {
    return window.requestIdleCallback(() => fn(), { timeout });
  }
  return window.setTimeout(fn, 0);
};

/**
 * prefecture 正規化
 * - null/空は "未設定"
 * - 前後空白除去
 */
const normalizePrefecture = (v) => {
  const s = String(v ?? "").trim();
  return s ? s : "未設定";
};

/**
 * country 正規化
 * - null/空は ""
 * - 前後空白除去
 */
const normalizeCountry = (v) => {
  const s = String(v ?? "").trim();
  return s;
};

export default function MusicGeoPrefectureStats({
  trackId,
  accentColor,
  lowPower = false,
  refreshToken = 0,
}) {
  const theme = useContext(AppThemeContext) || {};
  const accent = accentColor || theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const { buildDateKey } = useDayBoundary();
  const textBase = theme.railText || "rgba(30,30,60,0.9)";

  // 集計保持
  const [byPref, setByPref] = useState({});
  const [byCountry, setByCountry] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);

  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [todayKey, setTodayKey] = useState(buildDateKey(new Date()));

  // 0時跨ぎ対策
  useEffect(() => {
    const tick = () => {
      const k = buildDateKey(new Date());
      setTodayKey((prev) => (prev === k ? prev : k));
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // 同一キーでの再取得抑制（成功時のみ）
  const lastCompletedKeyRef = useRef("");

  useEffect(() => {
    if (!trackId || !todayKey) return;

    let cancelled = false;

    const aggId = `${trackId}_${todayKey}`;
    const aggRef = doc(db, "musicDailyAggregates", aggId);

    const fetchOnce = async () => {
      try {
        const key = `${aggId}__${refreshToken}`;

        if (lastCompletedKeyRef.current === key) return;

        const snap = await getDoc(aggRef);
        if (cancelled) return;

        if (!snap.exists()) {
          setByPref({});
          setByCountry({});
          setTotalUsers(0);
          setHasFetchedOnce(true);
          lastCompletedKeyRef.current = key;
          return;
        }

        const data = snap.data() || {};

        const c = typeof data.count === "number" ? data.count : 0;

        const rawByPref =
          data.byPref && typeof data.byPref === "object" ? data.byPref : {};

        const rawByCountry =
          data.byCountry && typeof data.byCountry === "object"
            ? data.byCountry
            : {};

        await yieldToMain();

        const nextByPref = {};
        for (const [k, v] of Object.entries(rawByPref)) {
          const pref = normalizePrefecture(k);
          const n = Number(v);
          if (!Number.isFinite(n) || n <= 0) continue;
          nextByPref[pref] = (nextByPref[pref] || 0) + Math.floor(n);
        }

        const nextByCountry = {};
        for (const [k, v] of Object.entries(rawByCountry)) {
          const country = normalizeCountry(k);
          const n = Number(v);
          if (!country) continue;
          if (!Number.isFinite(n) || n <= 0) continue;
          nextByCountry[country] =
            (nextByCountry[country] || 0) + Math.floor(n);
        }

        setByPref(nextByPref);
        setByCountry(nextByCountry);
        setTotalUsers(c);
        setHasFetchedOnce(true);
        lastCompletedKeyRef.current = key;
      } catch (e) {
        console.error("musicDailyAggregates getDoc error:", e);
      }
    };

    const idleId = runWhenIdle(() => {
      if (!cancelled) fetchOnce();
    }, 1500);

    return () => {
      cancelled = true;
      try {
        if (
          typeof window !== "undefined" &&
          "cancelIdleCallback" in window &&
          typeof idleId === "number"
        ) {
          window.cancelIdleCallback(idleId);
        } else if (typeof idleId === "number") {
          clearTimeout(idleId);
        }
      } catch (_) {}
    };
  }, [trackId, todayKey, refreshToken]);

  // 日本地図表示用（海外/未設定は除外）
  const domesticCounts = useMemo(() => {
    const src = byPref || {};
    const out = {};
    for (const [pref, count] of Object.entries(src)) {
      const p = normalizePrefecture(pref);
      const n = Number(count) || 0;
      if (!n) continue;

      if (p.startsWith("海外") || p === "未設定") continue;

      out[p] = n;
    }
    return out;
  }, [byPref]);

  // 海外（アメリカ）
  const usaCount = useMemo(() => {
    return Number(byPref?.["海外（アメリカ）"] || 0);
  }, [byPref]);

  // 海外（その他）の国別
  const overseasCountryCounts = useMemo(() => {
    return byCountry || {};
  }, [byCountry]);

  if (!trackId) return null;

  return (
    <Box
      sx={{
        mt: 2,
        mb: 3,
        borderRadius: 4,
        border: `1px solid ${border}`,
        bgcolor: surface,
        p: { xs: 2, sm: 2.5 },
        maxWidth: 460,
        mx: "auto",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip
          icon={<PublicRoundedIcon />}
          label="光の音柱"
          size="small"
          sx={{
            bgcolor: `${accent}10`,
            color: accent,
            fontWeight: 600,
            letterSpacing: "0.05em",
          }}
        />

        <Typography variant="body2" sx={{ opacity: 0.8, color: textBase }}>
          音の柱が図に表示されます（※国内都道府県のみ表示。このマップは、4月中までに非表示となります）
        </Typography>
      </Stack>

      <Box
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 18px 40px rgba(0,0,0,0.14)",
        }}
      >
        <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
          <Chip
            label={isDark ? "明るい地図" : "暗い地図"}
            size="small"
            onClick={() => setIsDark((v) => !v)}
            sx={{ borderRadius: 999, bgcolor: "rgba(255,255,255,0.95)" }}
          />
        </Box>

        <Box sx={{ width: "100%", height: 260 }}>
          <MusicPrefectureMap
            prefectureCounts={domesticCounts}
            accentColor={accent}
            darkMode={isDark}
            lowPower={lowPower}
            disablePulse={false}
          />
        </Box>

        {!hasFetchedOnce && <OverlayText text="集計中…" />}

        {hasFetchedOnce && (totalUsers || 0) === 0 && (
          <OverlayText text="今日はまだ、この曲を再生した仲間はいません。" />
        )}
      </Box>

      {/* デバッグ兼つなぎ確認用。後でUI化する */}
      {(usaCount > 0 || Object.keys(overseasCountryCounts).length > 0) && (
        <Box sx={{ mt: 1.5 }}>
          {usaCount > 0 && (
            <Typography variant="caption" sx={{ display: "block", color: textBase, opacity: 0.8 }}>
              海外（アメリカ）: {usaCount}人
            </Typography>
          )}

          {Object.keys(overseasCountryCounts).length > 0 && (
            <Typography variant="caption" sx={{ display: "block", color: textBase, opacity: 0.8 }}>
              海外（その他）国数: {Object.keys(overseasCountryCounts).length}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function OverlayText({ text }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 12,
        right: 12,
        bottom: 10,
        bgcolor: "rgba(255,255,255,0.92)",
        borderRadius: 2,
        px: 1.2,
        py: 0.8,
        boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        {text}
      </Typography>
    </Box>
  );
}